import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { COMPANY_INFO } from '../lib/utils';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { VIEWPORT_ONCE } from '../lib/motion';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.03 } } };

// Villes couvertes dans un rayon de 40km autour de Servins (62530)
const ZONES = [
  {
    region: 'Agglomération de Lens-Liévin',
    cities: ['Lens', 'Liévin', 'Avion', 'Méricourt', 'Sallaumines', 'Billy-Montigny', 'Wingles', 'Harnes', 'Bully-les-Mines', 'Noyelles-sous-Lens', 'Grenay', 'Loos-en-Gohelle', 'Angres', 'Givenchy-en-Gohelle', 'Souchez', 'Ablain-Saint-Nazaire', 'Carency', 'Vimy']
  },
  {
    region: 'Agglomération de Béthune-Bruay',
    cities: ['Béthune', 'Bruay-la-Buissière', 'Noœux-les-Mines', 'Barlin', 'Auchel', 'Lillers', 'Houdain', 'Hersin-Coupigny', 'Marles-les-Mines', 'Divion', 'Labourse', 'Beuvry', 'Annezin', 'Fouquières-lès-Béthune', 'Verquin', 'Hesdigneul-lès-Béthune']
  },
  {
    region: 'Arrageois',
    cities: ['Arras', 'Saint-Laurent-Blangy', 'Saint-Nicolas', 'Sainte-Catherine', 'Anzin-Saint-Aubin', 'Achicourt', 'Beaurains', 'Dainville', 'Athies', 'Fampoux', 'Roclincourt', 'Neuville-Saint-Vaast', 'Thélus', 'Marœuil', 'Mont-Saint-Éloi', 'Aubigny-en-Artois']
  },
  {
    region: 'Bassin minier & Hénin-Carvin',
    cities: ['Hénin-Beaumont', 'Carvin', 'Libercourt', 'Oignies', 'Courrières', 'Leforest', 'Montigny-en-Gohelle', 'Drocourt', 'Rouvroyvi', 'Évin-Malmaison', 'Courcelles-lès-Lens', 'Noyelles-Godault']
  },
  {
    region: 'Ternois & campagne',
    cities: ['Saint-Pol-sur-Ternoise', 'Frévent', 'Avesnes-le-Comte', 'Pernes', 'Bours', 'Amettes', 'Tangry', 'Cambligneul', 'Camblain-l\'Abbé', 'Gouy-Servins', 'Villers-au-Bois', 'Bouvigny-Boyeffles', 'Servins']
  },
  {
    region: 'Secteur Douaisis (limite)',
    cities: ['Douai', 'Cuincy', 'Lambres-lez-Douai', 'Flers-en-Escrebieux', 'Esquerchin', 'Izel-lès-Équerchin']
  }
];

const totalCities = ZONES.reduce((acc, z) => acc + z.cities.length, 0);

const SERVINS_LAT = 50.3816;
const SERVINS_LNG = 2.6911;
const RADIUS_KM = 40;

function InterventionMap() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (mapInstanceRef.current) return; // already initialized

    const map = L.map(mapRef.current, {
      center: [SERVINS_LAT, SERVINS_LNG],
      zoom: 9,
      scrollWheelZoom: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);

    // Circle for the 40km zone
    L.circle([SERVINS_LAT, SERVINS_LNG], {
      radius: RADIUS_KM * 1000,
      color: '#3a8f8f',
      fillColor: '#3a8f8f',
      fillOpacity: 0.12,
      weight: 3,
      dashArray: '10, 8',
    }).addTo(map).bindPopup('<strong>Zone d\'intervention</strong><br/>Rayon de 40 km autour de Servins');

    // Marker for Servins HQ
    const icon = L.divIcon({
      className: '',
      html: '<div style="background:#3a8f8f;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });

    L.marker([SERVINS_LAT, SERVINS_LNG], { icon })
      .addTo(map)
      .bindPopup('<strong>Ptitservices.com</strong><br/>61 rue de la mairie<br/>62530 Servins');

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return <div ref={mapRef} style={{ width: '100%', height: '420px', borderRadius: '0.9rem' }} />;
}

export default function ZonePage() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Zone d'intervention</span>
          </div>
          <Badge className="mb-3 bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] hover:bg-[hsl(var(--brand-mint))]">Zone Artois</Badge>
          <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Notre zone d'intervention &mdash; Artois et environs</h1>
          <p className="text-muted-foreground max-w-2xl text-base sm:text-lg">Ptitservices.com intervient dans un rayon de 40 kilomètres autour de Servins (62530), couvrant plus de {totalCities} communes de l'Artois, du bassin minier et de l'Arrageois.</p>
        </div>
      </section>

      {/* Map */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-4">Localisation</h2>
              <Card className="overflow-hidden border border-border shadow-sm">
                <InterventionMap />
              </Card>
              <div className="mt-4 p-4 rounded-lg bg-[hsl(var(--brand-mint))]/30">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-5 w-5 text-[hsl(var(--primary))]" />
                  <span className="font-semibold text-sm">Siège : {COMPANY_INFO.fullAddress}</span>
                </div>
                <p className="text-xs text-muted-foreground">Rayon d'intervention : 40 km autour de Servins &bull; Pas-de-Calais (62) &bull; Région Hauts-de-France</p>
              </div>
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-4">Nos engagements locaux</h2>
              <div className="space-y-3 mb-6">
                <Card className="p-4 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm">Intervention rapide</h3>
                    <p className="text-xs text-muted-foreground">Proximité garantie pour des interventions réactives dans toute la zone Artois.</p>
                  </div>
                </Card>
                <Card className="p-4 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm">Easers locaux</h3>
                    <p className="text-xs text-muted-foreground">Nos Easers habitent dans le secteur. Ils connaissent la région et s'engagent dans la durée.</p>
                  </div>
                </Card>
                <Card className="p-4 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm">Devis gratuit à domicile</h3>
                    <p className="text-xs text-muted-foreground">Nous nous déplaçons gratuitement chez vous pour évaluer vos besoins et établir un devis personnalisé.</p>
                  </div>
                </Card>
              </div>
              <Link to="/devis">
                <Button className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                  Vérifier ma commune et demander un devis
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cities list */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Villes et communes desservies</h2>
            <p className="text-muted-foreground">Plus de {totalCities} communes couvertes dans un rayon de 40 km autour de Servins</p>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {ZONES.map((zone) => (
              <motion.div key={zone.region} variants={fadeUp}>
                <Card className="p-5 h-full">
                  <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3 text-[hsl(var(--primary))]">{zone.region}</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {zone.cities.map((city) => (
                      <Badge key={city} variant="secondary" className="text-xs font-normal">{city}</Badge>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-8 p-5 rounded-xl bg-[hsl(var(--brand-mint))]/30 text-center">
            <p className="text-sm text-muted-foreground mb-3">Votre commune n'apparaît pas dans la liste ? Contactez-nous pour vérifier si nous intervenons chez vous.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={`tel:${COMPANY_INFO.phoneRaw}`}>
                <Button variant="outline" size="sm">
                  <Phone className="mr-2 h-4 w-4" />
                  {COMPANY_INFO.phone}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="sm">Nous contacter</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Services à la personne dans l'Artois</h2>
          <p className="text-primary-foreground/80 mb-8">Ménage, jardinage, repassage, petit bricolage — tous éligibles au crédit d'impôt 50%. Devis gratuit et sans engagement.</p>
          <Link to="/devis">
            <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">
              Demander un devis gratuit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
